import { useTranslation } from 'react-i18next';

import { DataPanel, DataPanelAutoWrapInlineItem, DataPanelItem, DataPanelRow, NgButton } from '@/ui';
import type { EedCallCapabilities, EedCallInformation, EedCallerInformation } from '../../api/incidentDetailApi';
import { formatEedHeight, formatEedWeight } from '../../model/formatters';
import { ClassificationPanelItem, type ClassificationItem } from '../ClassificationPanelItem';
import { EedEmergencyContactPanel } from './EedEmergencyContactPanel';

export type EedCallerInfoPanelProps = {
  incidentId: string;
  eedCallInformation: EedCallInformation | null;
  eedMedicalData: EedCallerInformation | null;
  eedCallCapabilities: readonly EedCallCapabilities[];
  askedForEedMedicalData: boolean;
  askedForEedMedia: boolean;
  getEedMedicalData: () => void | Promise<void>;
  requestEedMedia: () => void | Promise<void>;
};

export function EedCallerInfoPanel({
  eedCallInformation,
  eedMedicalData,
  eedCallCapabilities,
  askedForEedMedicalData,
  askedForEedMedia,
  getEedMedicalData,
  requestEedMedia,
}: EedCallerInfoPanelProps) {
  const { t } = useTranslation();

  if (!eedCallCapabilities.some((capability) => capability.hasAdditionalData)) {
    return null;
  }

  const classificationItems: ClassificationItem[] = [];
  if (eedCallInformation?.trigger_type === 'Fall Detection') {
    classificationItems.push({
      title: t('Incident_InfoPanel_Eed_FallDetection'),
      image: 'Images/Incident/EED/fall_detection.svg',
    });
  }
  if (eedCallInformation?.trigger_type === 'Crash Detection') {
    classificationItems.push({
      title: t('Incident_InfoPanel_Eed_CrashDetection'),
      image: 'Images/Incident/EED/crash_detection.svg',
    });
  }

  return (
    <div>
      {eedMedicalData !== null ? (
        <DataPanel header="Apple EED" expandable>
          <ClassificationPanelItem items={classificationItems} />

          <DataPanelRow>
            <DataPanelItem title={t('Incident_InfoPanel_Age')} value={eedMedicalData.age?.toString()} />

            <DataPanelItem title={t('Incident_InfoPanel_CallerHeight')} value={formatEedHeight(eedMedicalData.height)} />

            <DataPanelItem title={t('Incident_InfoPanel_CallerWeight')} value={formatEedWeight(eedMedicalData.weight)} />
          </DataPanelRow>

          <DataPanelAutoWrapInlineItem
            title={t('Incident_InfoPanel_Name')}
            value={eedMedicalData.name ?? undefined}
            showEmptyPlaceholder
          />
          <DataPanelAutoWrapInlineItem
            title={t('Incident_InfoPanel_Eed_GestationalAge')}
            value={eedMedicalData.gestational_age ?? undefined}
            showEmptyPlaceholder
          />
          <DataPanelAutoWrapInlineItem
            title={t('Incident_InfoPanel_Eed_Allergies')}
            value={eedMedicalData.allergies ?? undefined}
            showEmptyPlaceholder
          />
          <DataPanelAutoWrapInlineItem
            title={t('Incident_InfoPanel_Eed_BloodType')}
            value={eedMedicalData.blood_type ?? undefined}
            showEmptyPlaceholder
          />
          <DataPanelAutoWrapInlineItem
            title={t('Incident_InfoPanel_Eed_Medications')}
            value={eedMedicalData.medications ?? undefined}
            showEmptyPlaceholder
          />
          <DataPanelAutoWrapInlineItem
            title={t('Incident_InfoPanel_Eed_MedicalConditions')}
            value={eedMedicalData.medical_conditions ?? undefined}
            showEmptyPlaceholder
          />
          <DataPanelAutoWrapInlineItem
            title={t('Incident_InfoPanel_Eed_MedicalNotes')}
            value={eedMedicalData.medical_notes ?? undefined}
            showEmptyPlaceholder
          />

          <EedEmergencyContactPanel contacts={eedMedicalData.emergency_contacts} />
        </DataPanel>
      ) : askedForEedMedicalData ? (
        <div className="d-flex flex-column align-items-center gap-2 p-2 bg-chat-dispatcher medium text-center">
          {t('Incident_InfoPanel_Eed_NoEedAdditionalData')}
        </div>
      ) : (
        <div className="d-flex flex-column align-items-center gap-2 p-2 bg-chat-dispatcher medium text-center">
          {t('Incident_InfoPanel_Eed_RetrieveInfo_Line1')}
          <NgButton
            text={t('Incident_InfoPanel_Eed_RetrieveInfo')}
            onClick={() => void getEedMedicalData()}
            enableMultipleClickPrevention
            showProgress
          />
        </div>
      )}

      {eedCallCapabilities.some((capability) => capability.hasMediaUploadCapability) && (
        <div className="d-flex flex-column align-items-center gap-2 pt-3 p-2 bg-chat-dispatcher medium text-center">
          <NgButton
            text={t('Incident_InfoPanel_Eed_RequestMedia')}
            onClick={() => void requestEedMedia()}
            icon="fal fa-image"
            enableMultipleClickPrevention
            showProgress
          />
          {askedForEedMedia && <div>{t('Incident_InfoPanel_Eed_MediaRequested')}</div>}
        </div>
      )}
    </div>
  );
}
